import { ReactNode, useEffect, useState } from "react";
import {
  StepupFrequency,
  STEP_UP_FREQUENCY_OPTIONS,
} from "creso-core/constants";
import {
  V2Label,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  CurrencyInput,
  Input,
  ToggleGroup,
  ToggleGroupItem,
  cn,
} from "web-design-system";

export type StepUpSipUnit = "AMOUNT" | "PERCENTAGE";

export type StepUpSipValue = {
  amount: number | null;
  percentage: number | null;
  frequency: StepupFrequency | null;
};

export const DEFAULT_STEP_UP_SIP_VALUE: StepUpSipValue = {
  amount: null,
  percentage: null,
  frequency: StepupFrequency.HALFYEARLY,
};

const STEP_UP_UNIT_OPTIONS: { label: string; value: StepUpSipUnit }[] = [
  { label: "%", value: "PERCENTAGE" },
  { label: "₹", value: "AMOUNT" },
];

type StepUpSipFieldsProps = {
  value: StepUpSipValue;
  /** "card" renders a bordered card with the switch on the right */
  variant?: "default" | "card";
  description?: ReactNode;
  frequencyOptions?: { label: string; value: StepupFrequency }[];
  /** Frequency selected when the switch is turned on */
  defaultFrequency?: StepupFrequency;
  onChange: (value: StepUpSipValue) => void;
  disabled?: boolean;
  nextStepUpDate?: string;
  className?: string;
  errors?: {
    amount?: string;
    percentage?: string;
  };
};

export function StepUpSipConfigComponent({
  value,
  onChange,
  disabled,
  nextStepUpDate,
  className,
  errors,
  variant = "default",
  description,
  frequencyOptions = STEP_UP_FREQUENCY_OPTIONS,
  defaultFrequency = StepupFrequency.HALFYEARLY,
}: StepUpSipFieldsProps) {
  const [enabled, setEnabled] = useState(!!value.frequency);
  const [unit, setUnit] = useState<StepUpSipUnit>(
    value.amount ? "AMOUNT" : "PERCENTAGE"
  );

  // Keep the toggle/unit UI state in sync whenever the underlying value is
  // reset from outside (e.g. switching the selected order, reopening the
  // dialog with a different plan).
  useEffect(() => {
    setEnabled(!!value.frequency);
    if (value.amount) setUnit("AMOUNT");
    else if (value.percentage) setUnit("PERCENTAGE");
  }, [value.frequency, value.amount, value.percentage]);

  const isCard = variant === "card";

  const toggle = (
    <Switch
      checked={enabled}
      disabled={disabled}
      onCheckedChange={(checked) => {
        setEnabled(checked);
        setUnit("PERCENTAGE");
        onChange({
          amount: null,
          percentage: null,
          frequency: checked ? defaultFrequency : null,
        });
      }}
    />
  );

  return (
    <div
      className={cn(
        "flex flex-col gap-4",
        isCard && "p-4 bg-card rounded-lg border border-border",
        className
      )}
    >
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-3">
          <V2Label className="text-sm font-semibold">Step-up SIP</V2Label>
          {description && (
            <span className="text-xs text-muted-foreground">{description}</span>
          )}
          {!isCard && toggle}
        </div>
        {isCard && toggle}
      </div>
      {enabled && (
        <div className="grid grid-cols-10 w-full items-start gap-6">
          <div className="col-span-5 flex flex-col gap-1.5">
            <V2Label className="text-sm font-normal">Increase by</V2Label>
            <div className="flex flex-row items-start gap-2">
              <Tabs
                value={unit}
                onValueChange={(newUnit) => {
                  setUnit(newUnit as StepUpSipUnit);
                  onChange({
                    amount: null,
                    percentage: null,
                    frequency: value.frequency ?? defaultFrequency,
                  });
                }}
              >
                <TabsList className="h-9 px-0.5 bg-secondary">
                  {STEP_UP_UNIT_OPTIONS.map((option) => (
                    <TabsTrigger key={option.value} value={option.value}>
                      {option.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              {unit === "AMOUNT" ? (
                <div className="flex flex-col gap-1 w-full">
                  <CurrencyInput
                    value={value.amount ?? ""}
                    disabled={disabled}
                    placeholder="₹ Step-up amount"
                    onValueChange={(amount) =>
                      onChange({
                        amount: amount ? Number(amount) : null,
                        percentage: null,
                        frequency: value.frequency ?? defaultFrequency,
                      })
                    }
                  />
                  {errors?.amount && (
                    <p className="text-xs font-medium text-destructive">
                      {errors.amount}
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-1 w-full">
                  <div className="relative">
                    <Input
                      value={value.percentage ?? ""}
                      disabled={disabled}
                      placeholder="Step-up percentage"
                      className="pr-7"
                      onChange={(e) => {
                        const percentage = e.target.value.replace(
                          /[^0-9.]/g,
                          ""
                        );
                        onChange({
                          amount: null,
                          percentage: percentage ? Number(percentage) : null,
                          frequency: value.frequency ?? defaultFrequency,
                        });
                      }}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                      %
                    </span>
                  </div>
                  {errors?.percentage && (
                    <p className="text-xs font-medium text-destructive">
                      {errors.percentage}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="col-span-5 flex flex-col gap-1.5">
            <V2Label className="text-sm font-normal">Increase every</V2Label>
            <ToggleGroup
              type="single"
              value={value.frequency?.toString() ?? defaultFrequency.toString()}
              disabled={disabled}
              onValueChange={(frequency) => {
                if (!frequency) return;
                onChange({
                  ...value,
                  frequency: Number(frequency) as StepupFrequency,
                });
              }}
              className="w-fit gap-2 flex-wrap justify-start py-0 bg-transparent"
            >
              {frequencyOptions.map((option) => (
                <ToggleGroupItem
                  key={option.value}
                  value={option.value?.toString()}
                  variant="outline"
                  className="px-3 py-2 border-[1.5px] bg-background text-primary font-normal rounded-md data-[state=on]:border-primary data-[state=on]:font-semibold"
                >
                  {option.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <span
              className={cn(
                "text-xs text-muted-foreground invisible",
                nextStepUpDate && "visible"
              )}
            >
              Next step-up on {nextStepUpDate}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
